import * as PIXI from 'pixi.js';
import { globalEventBus, GameEvents } from '../core/EventBus.js';

/**
 * 图片加载工具
 * 支持URL加载和本地文件上传
 */
export class ImageLoader {
    constructor() {
        this.cache = new Map();
        this.events = globalEventBus;
    }

    /**
     * 加载图片
     * @param {string|File} source 图片URL或File对象
     * @returns {Promise<PIXI.Texture>} PIXI纹理
     */
    async load(source) {
        if (source instanceof File) {
            return this.loadFromFile(source);
        } else if (typeof source === 'string') {
            return this.loadFromUrl(source);
        } else {
            throw new Error('Invalid image source');
        }
    }

    /**
     * 从URL加载图片
     * @param {string} url 图片URL
     * @returns {Promise<PIXI.Texture>} PIXI纹理
     */
    async loadFromUrl(url) {
        // 检查缓存
        if (this.cache.has(url)) {
            return this.cache.get(url);
        }

        try {
            const texture = await PIXI.Assets.load(url);
            this.cache.set(url, texture);
            return texture;
        } catch (error) {
            console.error('Failed to load image from URL:', url, error);
            throw new Error(`Failed to load image: ${error.message}`);
        }
    }

    /**
     * 从File对象加载图片
     * @param {File} file 文件对象
     * @returns {Promise<PIXI.Texture>} PIXI纹理
     */
    async loadFromFile(file) {
        return new Promise((resolve, reject) => {
            // 验证文件类型
            if (!file.type.startsWith('image/')) {
                reject(new Error('Invalid file type. Please select an image file.'));
                return;
            }

            const reader = new FileReader();

            reader.onload = async (e) => {
                try {
                    const dataUrl = e.target.result;
                    const texture = await PIXI.Assets.load(dataUrl);
                    resolve(texture);
                } catch (error) {
                    reject(new Error(`Failed to create texture: ${error.message}`));
                }
            };

            reader.onerror = () => {
                reject(new Error('Failed to read file'));
            };

            reader.readAsDataURL(file);
        });
    }

    /**
     * 创建文件选择器并加载选中的图片
     * @returns {Promise<PIXI.Texture>} PIXI纹理
     */
    selectAndLoad() {
        return new Promise((resolve, reject) => {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = 'image/*';

            input.onchange = async (e) => {
                const file = e.target.files[0];
                if (!file) {
                    reject(new Error('No file selected'));
                    return;
                }

                try {
                    const texture = await this.loadFromFile(file);
                    resolve(texture);
                } catch (error) {
                    reject(error);
                }
            };

            input.click();
        });
    }

    /**
     * 从拖放事件加载图片
     * @param {DragEvent} event 拖放事件
     * @returns {Promise<PIXI.Texture>} PIXI纹理
     */
    async loadFromDrop(event) {
        event.preventDefault();

        const files = event.dataTransfer.files;
        if (files.length === 0) {
            throw new Error('No files dropped');
        }

        const file = files[0];
        return this.loadFromFile(file);
    }

    /**
     * 加载示例图片
     * @param {number} width 宽度
     * @param {number} height 高度
     * @returns {Promise<PIXI.Texture>} 渐变纹理
     */
    async loadPlaceholder(width = 400, height = 300) {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');

        // 创建渐变背景
        const gradient = ctx.createLinearGradient(0, 0, width, height);
        gradient.addColorStop(0, '#3498db');
        gradient.addColorStop(0.5, '#9b59b6');
        gradient.addColorStop(1, '#e74c3c');

        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, width, height);

        // 添加文字
        ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
        ctx.font = 'bold 24px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('Jigsaw Puzzle', width / 2, height / 2);

        return PIXI.Texture.from(canvas);
    }

    /**
     * 获取图片尺寸信息
     * @param {PIXI.Texture} texture PIXI纹理
     * @returns {Object} 尺寸信息
     */
    getImageInfo(texture) {
        return {
            width: texture.width,
            height: texture.height,
            aspectRatio: texture.width / texture.height
        };
    }

    /**
     * 清除缓存
     */
    clearCache() {
        this.cache.clear();
    }

    /**
     * 从缓存移除指定图片
     * @param {string} url 图片URL
     */
    removeFromCache(url) {
        if (this.cache.has(url)) {
            const texture = this.cache.get(url);
            texture.destroy(true);
            this.cache.delete(url);
        }
    }
}

export default ImageLoader;
